/**
 * Utilities for building and parsing `data-automation-identifier` strings used in automation tests.
 */

/**
 * Parses a `data-automation-identifier` string into its components:
 * - `base`: The core identifier (e.g., "button", "summary-item").
 * - `payload`: A record of additional key-value pairs (e.g., { 'product-id': '123' }).
 *
 * Handles cases where the identifier is empty, or where the base and payload are both optional.
 *
 * @param identifier - Raw `data-automation-identifier` string.
 * @returns Parsed object with optional `base` and `payload`.
 */
export const parseExistingAutomationIdentifier = (
    identifier: string
): { base?: string; payload?: Record<string, string> } => {
    if (!identifier?.trim()) {
        return { base: undefined, payload: undefined };
    }

    const payload: Record<string, string> = {};

    // Regex to split into individual parts, preserving `key=[value]` as a single part
    const parts = identifier.trim().match(/(?:[^\s=]+=\[[^\]]*\]|[^\s]+)/g) || [];

    let base: string | undefined = undefined;
    const payloadRegex = /^([\w-]+)=\[(.*)\]$/;

    for (const part of parts) {
        const payloadMatch = part.match(payloadRegex);
        if (payloadMatch) {
            const [, key, value] = payloadMatch;
            payload[key] = value;
        } else if (!base) {
            base = part;
        }
    }

    return {
        base: base || undefined,
        payload: Object.keys(payload).length > 0 ? payload : undefined,
    };
};

/**
 * Constructs a `data-automation-identifier` string from a base and optional payload.
 *
 * Examples:
 * - base: "button"
 * - payload: { label: "continue", foo: "bar" }
 * - Result: "button label=[continue] foo=[bar]"
 *
 * If both base and payload are missing, returns an empty string.
 *
 * @param base - Optional base identifier (e.g., "button").
 * @param payload - Optional key-value pairs to append to the identifier.
 * @returns Combined identifier string.
 */
export const buildAutomationIdentifier = (
    base?: string,
    payload?: Record<string, string>
): string => {
    if (base === undefined) {
        base = '';
    }

    if (payload) {
        const uniquePayload = Object.entries(payload).reduce<Record<string, string>>(
            (acc, [key, value]) => {
                if (value !== undefined) {
                    acc[key] = value;
                }
                return acc;
            },
            {}
        );

        const payloadString = Object.entries(uniquePayload)
            .map(([key, value]) => `${key}=[${value}]`)
            .join(' ');

        return `${base} ${payloadString}`.trim();
    }

    return base;
};

/**
 * Helper function that builds the `data-automation-identifier` attribute for a component.
 *
 * This is a convenience wrapper around `buildAutomationIdentifier`.
 * Example usage: `<button {...automation({ base: 'button', type: 'primary' })}>`
 *
 * @param base - Base identifier (e.g., "button").
 * @param payload - Additional key-value pairs.
 * @returns An object containing the `data-automation-identifier` attribute.
 */
export const automation = (opts: { base?: string; [key: string]: string | undefined }) => {
    const { base, ...rawPayload } = opts;
    const payload = Object.fromEntries(
        Object.entries(rawPayload).filter(([, value]) => value !== undefined)
    ) as Record<string, string>;
    const identifier = buildAutomationIdentifier(
        base,
        Object.keys(payload).length > 0 ? payload : undefined
    );

    return { 'data-automation-identifier': identifier };
};
